import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PhotoIcon, XMarkIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { adsApi, categoriesApi, fileUploadApi, UpdateAdRequest, Ad } from '../services/api';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../hooks/useConfirmationModal';
import { getImageUrl } from '../utils/imageUtils';

interface EditAdForm {
  title: string;
  description: string;
  price: number;
  categoryId: string;
  subCategoryId?: string;
  city: string;
  province: string;
  postalCode: string;
  address?: string;
  [key: string]: any; // Allow dynamic attribute fields
}

// Simple geocoding function
const geocodeAddress = async (city: string, province: string, postalCode: string): Promise<{ lat: number; lng: number }> => {
  try {
    const cityCoordinates: { [key: string]: { lat: number; lng: number } } = {
      'toronto': { lat: 43.6532, lng: -79.3832 },
      'vancouver': { lat: 49.2827, lng: -123.1207 },
      'montreal': { lat: 45.5017, lng: -73.5673 },
      'calgary': { lat: 51.0447, lng: -114.0719 },
      'ottawa': { lat: 45.4215, lng: -75.6972 },
      'edmonton': { lat: 53.5444, lng: -113.4909 },
      'winnipeg': { lat: 49.8951, lng: -97.1384 },
      'quebec city': { lat: 46.8139, lng: -71.2080 },
      'hamilton': { lat: 43.2557, lng: -79.8711 },
      'kitchener': { lat: 43.4516, lng: -80.4925 }
    };
    
    const normalizedCity = city.toLowerCase().trim();
    if (cityCoordinates[normalizedCity]) {
      return cityCoordinates[normalizedCity];
    }
    
    return { lat: 43.6532, lng: -79.3832 }; // Default to Toronto
  } catch (error) {
    console.error('Geocoding error:', error);
    return { lat: 43.6532, lng: -79.3832 };
  }
};

const EditAdPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [uploadingImages, setUploadingImages] = useState<boolean>(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  
  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<EditAdForm>();

  // Fetch the ad data
  const { data: adData, isLoading: isLoadingAd } = useQuery({
    queryKey: ['ad', id],
    queryFn: () => adsApi.getById(id!),
    enabled: !!id
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll()
  });

  // Fetch category attributes
  const { data: categoryAttributes } = useQuery({
    queryKey: ['category-attributes', selectedSubCategory || selectedCategory],
    queryFn: () => categoriesApi.getAttributes(selectedSubCategory || selectedCategory),
    enabled: !!(selectedSubCategory || selectedCategory)
  });

  // Fetch ad images
  const { data: adImages, refetch: refetchImages } = useQuery({
    queryKey: ['ad-images', id],
    queryFn: () => fileUploadApi.getAdImages(id!),
    enabled: !!id
  });

  // Update ad mutation
  const updateAdMutation = useMutation({
    mutationFn: (data: UpdateAdRequest) => adsApi.update(id!, data),
    onSuccess: () => {
      toast.success('Ad updated successfully!', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          direction: 'rtl',
          fontFamily: 'iranSans, sans-serif',
        }
      });
      queryClient.invalidateQueries({ queryKey: ['ad', id] });
      navigate(`/ad/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error updating the ad', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          direction: 'rtl',
          fontFamily: 'iranSans, sans-serif',
        }
      });
    }
  });

  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: ({ file, isPrimary }: { file: File; isPrimary: boolean }) => 
      fileUploadApi.uploadImage(id!, file, isPrimary),
    onSuccess: () => {
      toast.success('Image uploaded successfully!', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
      refetchImages();
      setUploadingImages(false);
      // Clear previews after successful upload
      setSelectedImages([]);
      setImagePreviews([]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error uploading image', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
      setUploadingImages(false);
    }
  });

  // Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: (imageId: string) => fileUploadApi.deleteImage(imageId),
    onSuccess: () => {
      toast.success('Image deleted successfully!', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
      refetchImages();
      setDeletingImageId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error deleting image', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
      setDeletingImageId(null);
    }
  });

  // Set primary image mutation
  const setPrimaryImageMutation = useMutation({
    mutationFn: (imageId: string) => fileUploadApi.setPrimaryImage(imageId),
    onSuccess: () => {
      toast.success('Primary image updated!', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
      refetchImages();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error setting primary image', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
    }
  });

  // Pre-fill form when ad data is loaded
  useEffect(() => {
    if (adData?.data) {
      const ad = adData.data;
      console.log('Loading ad data:', ad);
      console.log('Ad subcategoryId:', ad.subCategoryId);
      console.log('Ad categoryId:', ad.categoryId);
      
      setSelectedCategory(ad.categoryId);
      setSelectedSubCategory(ad.subCategoryId || '');
      
      // Create the form data object with all fields
      const formValues: any = {
        title: ad.title,
        description: ad.description,
        price: ad.price,
        categoryId: ad.categoryId,
        subCategoryId: ad.subCategoryId || '',
        city: ad.city,
        province: ad.province,
        postalCode: ad.postalCode,
        address: ad.address || '',
      };

      // Pre-fill category attributes with existing values
      if (ad.attributes && ad.attributes.length > 0) {
        console.log('Loading ad attributes:', ad.attributes);
        ad.attributes.forEach(attr => {
          console.log(`Setting attr_${attr.id} = ${attr.value}`);
          formValues[`attr_${attr.id}`] = attr.value;
        });
      }

      reset(formValues);
    }
  }, [adData, reset]);

  // Additional effect to ensure subcategory is set properly after categories are loaded
  useEffect(() => {
    if (adData?.data && categories?.data && adData.data.subCategoryId) {
      const ad = adData.data;
      const subCategoryId = ad.subCategoryId;
      console.log('Setting subcategory:', subCategoryId);
      console.log('Available categories:', categories.data.length);
      console.log('Selected category:', selectedCategory);
      
      // Verify that the subcategory exists in the available categories
      const subCategoryExists = categories.data.find(cat => cat.id === subCategoryId);
      console.log('Subcategory exists:', subCategoryExists);
      
      if (subCategoryExists && subCategoryId) {
        console.log('Setting subcategory state and form value');
        setSelectedSubCategory(subCategoryId);
        setValue('subCategoryId', subCategoryId);
        console.log('Subcategory set successfully:', subCategoryId);
      }
    }
  }, [adData, categories, selectedCategory, setValue]);

  const onSubmit = async (data: EditAdForm) => {
    try {
      // Get coordinates for the location
      const coordinates = await geocodeAddress(data.city, data.province, data.postalCode);
      
      const updateData: UpdateAdRequest = {
        title: data.title.trim(),
        description: data.description.trim(),
        price: Number(data.price),
        categoryId: selectedCategory,
        subCategoryId: selectedSubCategory || undefined,
        city: data.city.trim(),
        province: data.province,
        postalCode: data.postalCode.trim(),
        address: data.address?.trim() || `${data.city.trim()}, ${data.province}`,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        attributes: categoryAttributes?.data?.reduce((acc: any, attr) => {
          const value = (data as any)[`attr_${attr.id}`];
          if (value !== undefined && value !== '') {
            acc[attr.id] = value;
          }
          return acc;
        }, {}) || {}
      };

      updateAdMutation.mutate(updateData);
    } catch (error) {
      console.error('Error in onSubmit:', error);
      toast.error('Error processing location. Please try again.', {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
    }
  };

  const handleImageUpload = async (files: FileList) => {
    if (!files.length) return;
    
    const fileArray = Array.from(files);
    const maxFiles = 5;
    const currentImageCount = (adImages?.data?.length || 0) + selectedImages.length;
    
    if (currentImageCount + fileArray.length > maxFiles) {
      toast.error(`You can only upload up to ${maxFiles} images total.`, {
        style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
      });
      return;
    }
    
    // Validate file types and sizes
    const validFiles: File[] = [];
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file.`, {
          style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
        });
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`, {
          style: { direction: 'rtl', fontFamily: 'iranSans, sans-serif' }
        });
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) return;
    
    // Create previews for valid files first
    const newPreviews: string[] = [];
    const previewPromises = validFiles.map((file) => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      });
    });
    
    // Wait for all previews to be created
    const previews = await Promise.all(previewPromises);
    setSelectedImages(prev => [...prev, ...validFiles]);
    setImagePreviews(prev => [...prev, ...previews]);
    
    // Upload images after previews are created
    setUploadingImages(true);
    const isPrimary = !adImages?.data?.length; // First image is primary if no images exist
    
    try {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        await uploadImageMutation.mutateAsync({ 
          file, 
          isPrimary: isPrimary && i === 0 
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };
  
  const removeImagePreview = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteImage = (imageId: string) => {
    showModal({
      message: 'Are you sure you want to delete this image?',
      confirmText: 'OK',
      cancelText: 'Cancel',
      type: 'warning',
      isRtl: true,
      onConfirm: () => {
        setDeletingImageId(imageId);
        deleteImageMutation.mutate(imageId);
      }
    });
  };

  const handleSetPrimaryImage = (imageId: string) => {
    setPrimaryImageMutation.mutate(imageId);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
    setValue('categoryId', categoryId);
  };

  const availableSubCategories = categories?.data?.find(cat => cat.id === selectedCategory)?.subCategories || [];

  // Debug effect to track availableSubCategories
  useEffect(() => {
    console.log('Selected category changed:', selectedCategory);
    console.log('Available subcategories:', availableSubCategories.length);
    console.log('Current selected subcategory:', selectedSubCategory);
    if (availableSubCategories.length > 0) {
      console.log('Subcategories:', availableSubCategories.map(sub => ({ id: sub.id, name: sub.name })));
    }
  }, [selectedCategory, availableSubCategories, selectedSubCategory]);

  // Debug effect to track ad images
  useEffect(() => {
    console.log('Ad images data:', adImages);
    if (adImages?.data) {
      console.log('Number of images:', adImages.data.length);
      adImages.data.forEach((img, index) => {
        console.log(`Image ${index + 1}:`, img);
      });
    }
  }, [adImages]);

  if (isLoadingAd) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="bg-gray-200 rounded h-8 w-1/4"></div>
            <div className="bg-gray-200 rounded-lg h-96"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!adData?.data) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h1 className="text-2xl font-semibold text-red-600 mb-4">Ad not found</h1>
            <p className="text-gray-600 mb-6">The ad you are trying to edit does not exist or you do not have permission to edit it.</p>
            <button
              onClick={() => navigate('/my-ads')}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
            >
              Back to my ads
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">Edit Ad</h1>
              <button
                onClick={() => navigate('/my-ads')}
                className="text-gray-600 hover:text-gray-800"
              >
                ← Back to my ads
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8">
            {/* Images Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Images
              </label>
              
              {/* Existing Images */}
              {adImages?.data && adImages.data.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {adImages.data.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={getImageUrl(image.url)}
                        alt="Ad image"
                        className="w-full h-32 object-cover rounded-lg border"
                        onError={(e) => {
                          console.log('Image failed to load:', image.url);
                          console.log('Full URL tried:', getImageUrl(image.url));
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5Y2EzYWYiIGZvbnQtZmFtaWx5PSJtb25vc3BhY2UiIGZvbnQtc2l6ZT0iMTNweCI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg==';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', image.url);
                        }}
                      />
                      {image.isPrimary && (
                        <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs px-2 py-1 rounded">
                          Primary
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!image.isPrimary && (
                          <button
                            type="button"
                            onClick={() => handleSetPrimaryImage(image.id)}
                            className="bg-green-500 text-white p-1 rounded text-xs hover:bg-green-600"
                            title="Set as primary image"
                          >
                            ⭐
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(image.id)}
                          disabled={deletingImageId === image.id}
                          className="bg-red-500 text-white p-1 rounded hover:bg-red-600 disabled:opacity-50"
                          title="Delete image"
                        >
                          {deletingImageId === image.id ? (
                            <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full"></div>
                          ) : (
                            <TrashIcon className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Image Previews (Newly Selected) */}
              {imagePreviews.length > 0 && (
                <div className="mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImagePreview(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                        {uploadingImages && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                            <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {selectedImages.length} image(s) ready to upload
                  </p>
                </div>
              )}

              {/* Upload New Images */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="new-images" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      {uploadingImages ? 'Uploading...' : 'Add more images'}
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                      PNG, JPG, GIF up to 5MB each (max 5 files)
                    </span>
                    <input
                      id="new-images"
                      type="file"
                      multiple
                      accept="image/*"
                      className="sr-only"
                      disabled={uploadingImages}
                      onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  * Category
                </label>
                <div className="relative">
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                    style={{ 
                      backgroundImage: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none'
                    }}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories?.data?.filter(cat => !cat.parentCategoryId).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {availableSubCategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory
                  </label>
                  <div className="relative">
                    <select
                      {...register('subCategoryId')}
                      value={selectedSubCategory}
                      onChange={(e) => {
                        setSelectedSubCategory(e.target.value);
                        setValue('subCategoryId', e.target.value);
                      }}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                      style={{ 
                        backgroundImage: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none'
                      }}
                    >
                      <option value="">Select subcategory (optional)</option>
                      {availableSubCategories.map((subCategory) => (
                        <option key={subCategory.id} value={subCategory.id}>
                          {subCategory.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}
            </div>

            {/* Title and Description */}
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  * Title
                </label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="What are you selling?"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  * Description
                </label>
                <textarea
                  {...register('description', { required: 'Description is required' })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe your item in detail..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                      * Price (USD)
                    </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      {...register('price', {
                        required: 'Price is required',
                        min: { value: 0, message: 'Price must be greater than 0' }
                      })}
                      type="number"
                      step="0.01"
                      className="w-full pr-7 pl-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="10,000"
                    />
                  </div>
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Category-specific attributes */}
            {categoryAttributes?.data && categoryAttributes.data.length > 0 && (
                <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Specifications</h3>
                {categoryAttributes.data.map((attr) => (
                  <div key={attr.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {attr.isRequired && '*'} {attr.label}
                    </label>
                    {attr.type === 'Text' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} is required` : false
                        })}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'Number' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} is required` : false
                        })}
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'TextArea' && (
                      <textarea
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} is required` : false
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'Dropdown' && attr.options && (
                      <div className="relative">
                        <select
                          {...register(`attr_${attr.id}` as any, {
                              required: attr.isRequired ? `${attr.label} is required` : false
                            })}
                          className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                          style={{ 
                            backgroundImage: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none'
                          }}
                        >
                          <option value="">Select {attr.label}</option>
                          {(() => {
                            try {
                              return JSON.parse(attr.options).map((option: string) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ));
                            } catch {
                              return attr.options.split('\n').filter(opt => opt.trim()).map((option: string) => (
                                <option key={option.trim()} value={option.trim()}>
                                  {option.trim()}
                                </option>
                              ));
                            }
                          })()}
                        </select>
                        <ChevronDownIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    )}
                    {attr.type === 'Boolean' && (
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <input
                          {...register(`attr_${attr.id}` as any)}
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-600">{attr.label}</span>
                      </div>
                    )}
                    {attr.type === 'Date' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    )}
                    {attr.type === 'MultiSelect' && attr.options && (
                      <div className="space-y-2">
                        {(() => {
                          try {
                            return JSON.parse(attr.options).map((option: string) => (
                              <div key={option} className="flex items-center space-x-3 space-x-reverse">
                                <input
                                  {...register(`attr_${attr.id}` as any)}
                                  type="checkbox"
                                  value={option}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </div>
                            ));
                          } catch {
                            return attr.options.split('\n').filter(opt => opt.trim()).map((option: string) => (
                              <div key={option.trim()} className="flex items-center space-x-3 space-x-reverse">
                                <input
                                  {...register(`attr_${attr.id}` as any)}
                                  type="checkbox"
                                  value={option.trim()}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{option.trim()}</span>
                              </div>
                            ));
                          }
                        })()}
                      </div>
                    )}
                    {attr.type === 'Url' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false,
                          pattern: {
                            value: /^https?:\/\/.+/,
                            message: 'Enter a valid URL'
                          }
                        })}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
                    )}
                    {attr.type === 'Email' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Enter a valid email address'
                          }
                        })}
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="example@email.com"
                      />
                    )}
                    {errors[`attr_${attr.id}`] && (
                      <p className="mt-1 text-sm text-red-600">
                        {(errors[`attr_${attr.id}`] as any)?.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Location */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Location</h3>
              
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  {...register('address')}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="123 Main St (optional - will use city/province if not provided)"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    * City
                  </label>
                  <input
                    {...register('city', { required: 'City is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Toronto"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-2">
                    * Province
                  </label>
                  <div className="relative">
                    <select
                      {...register('province', { required: 'Province is required' })}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                      style={{ 
                        backgroundImage: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none'
                      }}
                    >
                      <option value="">Select province</option>
                      <option value="ON">Ontario</option>
                      <option value="QC">Quebec</option>
                      <option value="BC">British Columbia</option>
                      <option value="AB">Alberta</option>
                      <option value="MB">Manitoba</option>
                      <option value="SK">Saskatchewan</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="NT">Northwest Territories</option>
                      <option value="NU">Nunavut</option>
                      <option value="YT">Yukon</option>
                    </select>
                    <ChevronDownIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.province && (
                    <p className="mt-1 text-sm text-red-600">{errors.province.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                  * Postal Code
                </label>
                <input
                  {...register('postalCode', { required: 'Postal code is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="M5V 3L9"
                />
                {errors.postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.postalCode.message}</p>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/my-ads')}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || uploadingImages}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Updating...' : 'Update Ad'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'OK'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'warning'}
        isRtl={modalConfig?.isRtl || false}
      />
    </div>
  );
};

export default EditAdPage; 
